import { Plus } from 'lucide-react';
import { useCallback, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import type { OfferLetterView } from '@nest-recruit/shared';
import { Button } from '../../../components/Button';
import { Chip } from '../../../components/Chip';
import { SkeletonLines } from '../../../components/Skeleton';
import { EmptyState, ForbiddenState, ServerErrorState } from '../../../components/system-states/SystemState';
import { useUnsavedWarning } from '../../../lib/use-unsaved-warning';
import { useCanEdit, useCanView } from '../../../stores/session';
import { useOfferLetters } from '../api/offers-api';
import { LetterEditor, usageText, type StashedLetter } from '../components/letters/LetterEditor';
import styles from './OfferLettersPage.module.css';

/** The key a letter not saved yet is kept under. */
const NEW_KEY = 'new';

const CARD_KEYS: Readonly<Record<string, 1 | -1>> = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 };

/**
 * ADMIN-16 (DECISIONS-LOG S23 Q1) — the offer letters' wording. Admin redesign 2026-11-02, Option A of
 * `4-messages/offer-letters.html`: the letters as cards across the top, and the chosen one written on the left and
 * printed on the right as an A4 page. §L12's fields go in with "Insert a field"; the signatory is set on the letter.
 * Each job picks one in its offer settings, and an offer keeps its own copy of the wording, so changing a letter here
 * never changes an offer already made (non-negotiable #4).
 */
export function OfferLettersPage(): ReactNode {
  const canView = useCanView('hiring_setup');
  const canEdit = useCanEdit('hiring_setup');
  const letters = useOfferLetters(canView);
  const [selected, setSelected] = useState<string | null>(null);
  const [stash, setStash] = useState<ReadonlyMap<string, StashedLetter>>(() => new Map());
  const cards = useRef<HTMLDivElement | null>(null);
  // A new letter just saved is a saved letter now: what its editor leaves behind on the way out is not a draft.
  const justCreated = useRef(false);
  useUnsavedWarning(stash.size > 0);

  const remember = useCallback((key: string, draft: StashedLetter | null): void => {
    const isCreatedDraft = key === NEW_KEY && justCreated.current;
    if (isCreatedDraft) justCreated.current = false;
    setStash((current) => {
      if (isCreatedDraft) {
        if (!current.has(key)) return current;
        const next = new Map(current);
        next.delete(key);
        return next;
      }
      if (!draft && !current.has(key)) return current;
      const next = new Map(current);
      if (draft) next.set(key, draft);
      else next.delete(key);
      return next;
    });
  }, []);

  if (!canView) return <ForbiddenState resource="offer letters" contact="Your admin" />;
  if (letters.isPending) {
    return (
      <div className={styles.page}>
        <SkeletonLines count={10} />
      </div>
    );
  }
  // A failed load is a failure, never "No offer letters yet" (review of the old page). Letters already on screen stay.
  if (!letters.data) return <ServerErrorState onRetry={() => void letters.refetch()} />;

  const rows = letters.data;
  const chosen = selected ?? rows[0]?.id ?? null;
  // A letter is never removed (#6), but a stale choice still falls back to the first rather than to nothing.
  const letter = chosen === NEW_KEY ? null : (rows.find((row) => row.id === chosen) ?? rows[0] ?? null);
  const editingKey = chosen === NEW_KEY ? NEW_KEY : (letter?.id ?? null);

  /** 06-qol §2: the arrow keys move along the cards. */
  const onCardsKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    const step = CARD_KEYS[event.key];
    if (step === undefined) return;
    const buttons = Array.from(cards.current?.querySelectorAll<HTMLButtonElement>('[data-card]') ?? []);
    const at = buttons.findIndex((button) => button === document.activeElement);
    if (at === -1) return;
    event.preventDefault();
    buttons[(at + step + buttons.length) % buttons.length]?.focus();
  };

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div className={styles.heading}>
          <h1 className={styles.title}>
            Offer letters <span className={styles.count}>{rows.length}</span>
          </h1>
          <p className={styles.lede}>
            The wording of your offer letters. Each job chooses one in its offer settings; offers already made keep their own copy, so changing a letter here never changes an offer
            already sent.
          </p>
        </div>
        {canEdit && (
          <Button variant="solid" icon={Plus} onClick={() => setSelected(NEW_KEY)}>
            New letter
          </Button>
        )}
      </header>

      {rows.length === 0 && editingKey !== NEW_KEY ? (
        <EmptyState
          title="No offer letters yet"
          body="A job can't make an offer without one. Write your standard letter once — the fields fill in from each offer."
          action={
            canEdit ? (
              <Button variant="solid" icon={Plus} onClick={() => setSelected(NEW_KEY)}>
                Write the first letter
              </Button>
            ) : null
          }
        />
      ) : (
        <>
          <div ref={cards} className={styles.cards} role="group" aria-label="Your letters" onKeyDown={onCardsKeyDown}>
            {rows.map((row) => (
              <LetterCard key={row.id} letter={row} isSelected={row.id === editingKey} isUnsaved={stash.has(row.id)} onSelect={() => setSelected(row.id)} />
            ))}
            {canEdit && (
              <button type="button" data-card className={styles.newCard} aria-pressed={editingKey === NEW_KEY} onClick={() => setSelected(NEW_KEY)}>
                <Plus size={16} aria-hidden />
                New letter
                {stash.has(NEW_KEY) && <span className={styles.unsavedDot} role="img" aria-label="Unsaved changes" />}
              </button>
            )}
          </div>
          {editingKey !== null && (
            <LetterEditor
              key={editingKey}
              letter={editingKey === NEW_KEY ? null : letter}
              stashed={stash.get(editingKey)}
              canEdit={canEdit}
              knownIds={rows.map((row) => row.id)}
              onLeave={(draft) => remember(editingKey, draft)}
              onCreated={(id) => {
                justCreated.current = true;
                setSelected(id);
              }}
            />
          )}
        </>
      )}
    </div>
  );
}

function LetterCard({
  letter,
  isSelected,
  isUnsaved,
  onSelect,
}: {
  readonly letter: OfferLetterView;
  readonly isSelected: boolean;
  readonly isUnsaved: boolean;
  readonly onSelect: () => void;
}): ReactNode {
  return (
    <button type="button" data-card className={styles.card} aria-pressed={isSelected} onClick={onSelect}>
      <span className={styles.cardHead}>
        <span className={styles.cardName}>{letter.name}</span>
        {isUnsaved && <span className={styles.unsavedDot} role="img" aria-label="Unsaved changes" />}
        {!letter.isActive && (
          <Chip tone="ended" plain>
            Off
          </Chip>
        )}
      </span>
      <span className={styles.cardMeta}>
        {letter.ventureName ?? 'Every venture'} · {letter.signatoryName}, {letter.signatoryDesignation}
      </span>
      <span className={styles.cardMeta}>{usageText(letter)}</span>
    </button>
  );
}
